/**
 * Interceptor global no `window.fetch` para detectar falhas em chamadas de
 * refresh do Supabase Auth (`/auth/v1/token?grant_type=refresh_token`).
 *
 * Por quê: quando a rede está fora, abortada ou o gateway retorna 4xx/5xx,
 * o supabase-js NÃO dispara `SIGNED_OUT` nem `TOKEN_REFRESHED`. O fetch
 * simplesmente rejeita (ou resolve com status de erro) e a sessão "morta"
 * fica em memória, deixando rotas protegidas montadas.
 *
 * Este wrapper observa o resultado da chamada (não muda nada se sucesso)
 * e, em qualquer falha (rejeição, abort, timeout, status >= 400),
 * dispara `tearDownLocalSession("refresh_failed")` imediatamente.
 *
 * Instalado UMA vez no boot (main.tsx), antes do React montar.
 */
import { tearDownLocalSession } from "./teardown";

const REFRESH_PATH = "/auth/v1/token";
const REFRESH_GRANT = "grant_type=refresh_token";

const isRefreshTokenRequest = (
  input: RequestInfo | URL,
  init?: RequestInit,
): boolean => {
  try {
    const rawUrl =
      typeof input === "string"
        ? input
        : input instanceof URL
        ? input.toString()
        : (input as Request).url;
    if (!rawUrl || !rawUrl.includes(REFRESH_PATH)) return false;
    if (rawUrl.includes(REFRESH_GRANT)) return true;
    // Alguns clientes enviam o grant_type no body — checa init.body também.
    const body = init?.body ?? (input instanceof Request ? undefined : undefined);
    if (typeof body === "string" && body.includes(REFRESH_GRANT)) return true;
    return false;
  } catch {
    return false;
  }
};

let installed = false;
// Frente 12 — single-flight lock. Se múltiplas chamadas concorrentes
// dispararem refresh ao mesmo tempo (ex.: 3 RPCs com token expirado),
// reaproveitamos a MESMA Promise em vez de bater no /token N vezes
// (cada bater extra é mais um teardown e mais um 4xx no log).
let inflightRefresh: Promise<Response> | null = null;

export const installAuthRefreshInterceptor = () => {
  if (installed) return;
  if (typeof window === "undefined" || typeof window.fetch !== "function") return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const isRefresh = isRefreshTokenRequest(input, init);

    if (!isRefresh) {
      return originalFetch(input, init);
    }

    if (inflightRefresh) {
      // Já tem um refresh voando — clona a resposta para que múltiplos
      // consumidores possam ler o body independentemente.
      const shared = await inflightRefresh;
      return shared.clone();
    }

    inflightRefresh = (async () => {
      try {
        const response = await originalFetch(input, init);
        // Só derruba a sessão quando o servidor efetivamente disse que o
        // refresh_token é inválido/expirado (400/401 + payload conhecido).
        // Erros transitórios (429/5xx/timeout de gateway) NÃO devem
        // deslogar — o supabase-js tenta de novo automaticamente e o
        // usuário permanece na sessão por até 24h+ enquanto o refresh
        // token for válido.
        if (response.status === 400 || response.status === 401) {
          try {
            const cloned = response.clone();
            const text = await cloned.text();
            const lower = text.toLowerCase();
            const isFatal =
              lower.includes("invalid_grant") ||
              lower.includes("refresh_token_not_found") ||
              lower.includes("refresh token not found") ||
              lower.includes("invalid refresh token");
            if (isFatal) {
              void tearDownLocalSession("refresh_failed");
            }
          } catch {
            /* noop — se não conseguimos ler o body, não derruba por precaução */
          }
        }
        return response;
      } finally {
        // Falha de rede pura (offline, DNS, abort) sobe como erro e NÃO derruba
        // a sessão: o supabase-js volta a tentar quando a conexão voltar; e o
        // detectExpiredSessionAtBoot cuidará do caso terminal no próximo boot.
      }
    })();


    try {
      const response = await inflightRefresh;
      return response.clone();
    } finally {
      // Libera o lock no próximo tick para que requests realmente
      // sequenciais (não concorrentes) consigam novo refresh.
      queueMicrotask(() => { inflightRefresh = null; });
    }
  };
};
